import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { loyaltyPageSettingsSchema } from "./loyalty-page-builder/definition";
import {
  storefrontEmbeddingSchema,
  storefrontPopupSchema,
} from "./storefront-popup";

export enum LoyaltyEarningRuleType {
  PURCHASE = "purchase",
  ACCOUNT_CREATION = "accountCreation",
  INSTAGRAM = "instagram",
  FACEBOOK = "facebook",
  TIKTOK = "tiktok",
  BIRTHDAY = "birthday",
  REVIEW = "review",
}

export const LoyaltySocialMediaInteractionTypeSchema = createEnum(
  [
    LoyaltyEarningRuleType.INSTAGRAM,
    LoyaltyEarningRuleType.FACEBOOK,
    LoyaltyEarningRuleType.TIKTOK,
  ],
  {
    enumName: "LoyaltyEarningRuleType",
    isPartial: true,
    originalEnum: LoyaltyEarningRuleType,
  },
);
export type LoyaltySocialMediaInteractionType = z.infer<
  typeof LoyaltySocialMediaInteractionTypeSchema
>;

export const loyaltyEarningRuleBaseSchema = z.object({
  enabled: z.boolean(),
  type: createEnum(LoyaltyEarningRuleType, {
    enumName: "LoyaltyEarningRuleType",
  }),
});

export const loyaltyPurchaseRuleSchema = loyaltyEarningRuleBaseSchema.extend({
  type: z.literal(LoyaltyEarningRuleType.PURCHASE),
  pointsPerDollar: z.number().positive(),
});

export const loyaltyAccountCreationRuleSchema =
  loyaltyEarningRuleBaseSchema.extend({
    type: z.literal(LoyaltyEarningRuleType.ACCOUNT_CREATION),
    eventPoints: z.number().int().positive(),
  });

export const loyaltyInstagramRuleSchema = loyaltyEarningRuleBaseSchema.extend({
  type: z.literal(LoyaltyEarningRuleType.INSTAGRAM),
  eventPoints: z.number().int().positive(),
  instagramLink: z.url().optional(),
});

export const loyaltyFacebookRuleSchema = loyaltyEarningRuleBaseSchema.extend({
  type: z.literal(LoyaltyEarningRuleType.FACEBOOK),
  eventPoints: z.number().int().positive(),
  facebookLink: z.url().optional(),
});

export const loyaltyTiktokRuleSchema = loyaltyEarningRuleBaseSchema.extend({
  type: z.literal(LoyaltyEarningRuleType.TIKTOK),
  eventPoints: z.number().int().positive(),
  tiktokLink: z.url().optional(),
});

export const loyaltyBirthdayRuleSchema = loyaltyEarningRuleBaseSchema.extend({
  type: z.literal(LoyaltyEarningRuleType.BIRTHDAY),
  eventPoints: z.number().int().positive(),
});

export const loyaltyReviewRuleSchema = loyaltyEarningRuleBaseSchema.extend({
  type: z.literal(LoyaltyEarningRuleType.REVIEW),
  eventPoints: z.number().int().positive(),
});

export const loyaltyEarningRuleSchema = z.discriminatedUnion("type", [
  loyaltyPurchaseRuleSchema,
  loyaltyAccountCreationRuleSchema,
  loyaltyInstagramRuleSchema,
  loyaltyFacebookRuleSchema,
  loyaltyTiktokRuleSchema,
  loyaltyBirthdayRuleSchema,
  loyaltyReviewRuleSchema,
]);

export type LoyaltyEarningRule = z.infer<typeof loyaltyEarningRuleSchema>;
export type LoyaltyPurchaseRule = z.infer<typeof loyaltyPurchaseRuleSchema>;
export type LoyaltyAccountCreationRule = z.infer<
  typeof loyaltyAccountCreationRuleSchema
>;
export type LoyaltyInstagramRule = z.infer<typeof loyaltyInstagramRuleSchema>;
export type LoyaltyFacebookRule = z.infer<typeof loyaltyFacebookRuleSchema>;
export type LoyaltyTiktokRule = z.infer<typeof loyaltyTiktokRuleSchema>;
export type LoyaltyBirthdayRule = z.infer<typeof loyaltyBirthdayRuleSchema>;
export type LoyaltyReviewRule = z.infer<typeof loyaltyReviewRuleSchema>;

/**
 * Loyalty Configuration
 * pointValue: number of points per merchant currency (Divide points by this to get merchant currency value)
 */
export const loyaltyConfigurationSchema = z.object({
  _id: zExt.objectId(),
  enabled: z.boolean(),
  team: zExt.objectId(),
  pointValue: z.number().positive(),
  expirationMonths: z.number().int().positive(),
  earningRules: z.array(loyaltyEarningRuleSchema),
  excludedShopifyCollectionIds: z.array(z.string()).optional(),
  loyaltyPage: loyaltyPageSettingsSchema.optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type LoyaltyConfiguration = z.infer<typeof loyaltyConfigurationSchema>;

/* Loyalty Promotions */

export enum LoyaltyPointAdjustmentType {
  MULTIPLIER = "multiplier",
  BONUS = "bonus",
}

export enum LoyaltyPointAdjustmentReason {
  PROMOTION = "promotion",
  //TODO: Implement CUSTOMER_TIER = "customer-tier",
}

export enum LoyaltyPromotionRecurringPattern {
  NONE = "none",
  ALWAYS = "always",
  WEEKLY = "weekly",
  MONTHLY = "monthly",
}

export enum LoyaltyPromotionMonthlyWeek {
  FIRST = "1",
  SECOND = "2",
  THIRD = "3",
  FOURTH = "4",
  LAST = "5",
}

export enum LoyaltyPromotionMonthlyWeekday {
  MONDAY = "1",
  TUESDAY = "2",
  WEDNESDAY = "3",
  THURSDAY = "4",
  FRIDAY = "5",
  SATURDAY = "6",
  SUNDAY = "7",
}

export function isMonthlyPromotionActive(
  promotionWeekday: number,
  promotionWeek: LoyaltyPromotionMonthlyWeek,
) {
  const now = Temporal.Now.plainDateISO();
  const dayOfWeek = now.dayOfWeek;
  const dayOfMonth = now.day;
  if (promotionWeekday === dayOfWeek) {
    if (promotionWeek === LoyaltyPromotionMonthlyWeek.LAST) {
      const daysInMonth = now.daysInMonth;
      // If adding 7 days means we are past the end of the month, this is the last occurrence.
      return dayOfMonth + 7 > daysInMonth;
    } else {
      // Remove 7 days from the day of the month. Each successful step means we are one week further into the month.
      const daysToLookBack = 7 * (Number(promotionWeek) - 1);

      // If the day of the month minus the days to look back is still within the month, and one more step would be outside the month, this is the correct week.
      return (
        dayOfMonth - daysToLookBack >= 1 && dayOfMonth - daysToLookBack - 7 < 1
      );
    }
  }
  return false;
}

export enum LoyaltyPromotionConditionType {
  ALL_PURCHASES = "all-purchases",
  MINIMUM_ORDER_AMOUNT = "minimum-order-amount",
  FIRST_PURCHASE_ONLY = "first-purchase-only",
}

export const loyaltyPromotionConditionSchema = z.object({
  type: createEnum(
    [
      LoyaltyPromotionConditionType.ALL_PURCHASES,
      LoyaltyPromotionConditionType.MINIMUM_ORDER_AMOUNT,
      LoyaltyPromotionConditionType.FIRST_PURCHASE_ONLY,
    ],
    {
      enumName: "LoyaltyPromotionConditionType",
      isPartial: true,
      originalEnum: LoyaltyPromotionConditionType,
    },
  ),
});

export const loyaltyPromotionConditionAllPurchasesSchema =
  loyaltyPromotionConditionSchema.extend({
    type: z.literal(LoyaltyPromotionConditionType.ALL_PURCHASES),
  });

export const loyaltyPromotionConditionMinimumOrderAmountSchema =
  loyaltyPromotionConditionSchema.extend({
    type: z.literal(LoyaltyPromotionConditionType.MINIMUM_ORDER_AMOUNT),
    minimumOrderAmount: z.number().positive(),
  });

export const loyaltyPromotionConditionFirstPurchaseOnlySchema =
  loyaltyPromotionConditionSchema.extend({
    type: z.literal(LoyaltyPromotionConditionType.FIRST_PURCHASE_ONLY),
  });

export type LoyaltyPromotionCondition = z.infer<
  typeof loyaltyPromotionConditionSchema
>;

export type LoyaltyPromotionConditionMinimumOrderAmount = z.infer<
  typeof loyaltyPromotionConditionMinimumOrderAmountSchema
>;

export const loyaltyPromotionConditionUnionSchema = z.discriminatedUnion(
  "type",
  [
    loyaltyPromotionConditionAllPurchasesSchema,
    loyaltyPromotionConditionMinimumOrderAmountSchema,
    loyaltyPromotionConditionFirstPurchaseOnlySchema,
  ],
);

const weekDaySchema = z.number().min(1).max(7);
const weeklyDaysSchema = z.array(weekDaySchema);
const monthlyDaySchema = z.object({
  week: createEnum(
    [
      LoyaltyPromotionMonthlyWeek.FIRST,
      LoyaltyPromotionMonthlyWeek.SECOND,
      LoyaltyPromotionMonthlyWeek.THIRD,
      LoyaltyPromotionMonthlyWeek.FOURTH,
      LoyaltyPromotionMonthlyWeek.LAST,
    ],
    {
      enumName: "LoyaltyPromotionMonthlyWeek",
      isPartial: true,
      originalEnum: LoyaltyPromotionMonthlyWeek,
    },
  ),
  weekday: weekDaySchema,
});

// Recurring pattern schemas
const loyaltyPromotionRecurringBaseSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  name: z.string(),
  description: z.string(),
  type: createEnum(
    [LoyaltyPointAdjustmentType.MULTIPLIER, LoyaltyPointAdjustmentType.BONUS],
    {
      enumName: "LoyaltyPointAdjustmentType",
      isPartial: true,
      originalEnum: LoyaltyPointAdjustmentType,
    },
  ),
  value: z.number().positive(),
  active: z.boolean(),
  shopifyCollectionIds: z.array(z.string()).optional(),
  condition: loyaltyPromotionConditionUnionSchema,
  isDeleted: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
  startDate: z.date().optional(),
  endDate: z.date().optional(),
  weeklyDays: weeklyDaysSchema.optional(),
  monthlyDay: monthlyDaySchema.optional(),
});

export const loyaltyPromotionNoneRecurringSchema =
  loyaltyPromotionRecurringBaseSchema.extend({
    recurringPattern: z.literal(LoyaltyPromotionRecurringPattern.NONE),
    startDate: z.date(),
    endDate: z.date(),
  });

export const loyaltyPromotionAlwaysRecurringSchema =
  loyaltyPromotionRecurringBaseSchema.extend({
    recurringPattern: z.literal(LoyaltyPromotionRecurringPattern.ALWAYS),
  });

export const loyaltyPromotionWeeklyRecurringSchema =
  loyaltyPromotionRecurringBaseSchema.extend({
    recurringPattern: z.literal(LoyaltyPromotionRecurringPattern.WEEKLY),
    weeklyDays: weeklyDaysSchema,
  });

export const loyaltyPromotionMonthlyRecurringSchema =
  loyaltyPromotionRecurringBaseSchema.extend({
    recurringPattern: z.literal(LoyaltyPromotionRecurringPattern.MONTHLY),
    monthlyDay: monthlyDaySchema,
  });

export const loyaltyPromotionSchema = z.discriminatedUnion("recurringPattern", [
  loyaltyPromotionNoneRecurringSchema,
  loyaltyPromotionAlwaysRecurringSchema,
  loyaltyPromotionWeeklyRecurringSchema,
  loyaltyPromotionMonthlyRecurringSchema,
]);

export type LoyaltyPromotion = z.infer<typeof loyaltyPromotionSchema>;

export function defaultLoyaltyPromotion(
  teamId: string,
): Omit<LoyaltyPromotion, "_id" | "createdAt" | "updatedAt"> {
  const now = new Date();
  const oneMonthLater = new Date(now);
  oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);

  return {
    team: teamId,
    name: "Monthly Bonus Points",
    description: "Earn 20 points on every purchase for the next month.",
    type: LoyaltyPointAdjustmentType.BONUS,
    value: 20,
    active: true,
    shopifyCollectionIds: [],
    recurringPattern: LoyaltyPromotionRecurringPattern.NONE,
    startDate: now,
    endDate: oneMonthLater,
    condition: { type: LoyaltyPromotionConditionType.ALL_PURCHASES },
    isDeleted: false,
  };
}

export const loyaltyTeamSettingsSchema = z.object({
  enabled: z.boolean(),
  storefrontPopup: storefrontPopupSchema.optional(),
  storefrontEmbedding: storefrontEmbeddingSchema.optional(),
});

export type LoyaltyTeamSettings = z.infer<typeof loyaltyTeamSettingsSchema>;
